import * as THREE from 'three';

import {
  cloneTextureForMaterial,
  createArchitecturalWallMaterial,
  createFloorSurfaceMaterial,
  getCatalogTextureById,
} from './floorPlanThreeTextures.js';

import { getTextureCatalogEntry } from './floorPlanTextureCatalog.js';

import { normalizeMaterialConfig } from './floorPlanMaterialConfig.js';



/** @type {Map<string, THREE.MeshStandardMaterial>} */

const materialCache = new Map();



/**

 * @param {string} key

 */

function disposeCachedMaterial(key) {

  const existing = materialCache.get(key);

  if (!existing) return;

  if (existing.map) existing.map.dispose();

  existing.dispose();

  materialCache.delete(key);

}



/**

 * @param {{

 *   key: string;

 *   baseMap?: THREE.Texture | null;

 *   colorHex: string;

 *   shadeVariant?: 'normal' | 'dark';

 * }} params

 */

function getOrCreateMaterial({ key, baseMap = null, colorHex, shadeVariant = 'normal' }) {
  const cacheKey = `${key}:${shadeVariant}`;
  const cached = materialCache.get(cacheKey);
  if (cached) return cached;

  const material = createArchitecturalWallMaterial({
    map: baseMap ? cloneTextureForMaterial(baseMap) : null,
    colorHex,
    applyDarkTint: Boolean(baseMap && shadeVariant === 'dark'),
  });

  materialCache.set(cacheKey, material);
  return material;
}



/**

 * @param {import('./floorPlanThreeTextures.js').BaseTextureBundle | null | undefined} bundle

 * @param {string | null | undefined} textureId

 * @param {'inside' | 'outside'} classification

 * @returns {THREE.Texture | null}

 */

function resolveWallTextureMap(bundle, textureId, classification) {

  const catalogMap = getCatalogTextureById(textureId, bundle);

  if (catalogMap) return catalogMap;



  if (!bundle?.loaded) return null;

  if (textureId === 'wall-outside-default' || classification === 'outside') {

    return bundle.wallOutside ? cloneTextureForMaterial(bundle.wallOutside) : null;

  }

  return bundle.wallInside ? cloneTextureForMaterial(bundle.wallInside) : null;

}



/**

 * @param {import('./floorPlanThreeTextures.js').FloorPlanMaterialSet} materials

 * @param {import('./floorPlanThreeTextures.js').BaseTextureBundle | null | undefined} bundle

 * @param {import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig | null | undefined} config

 * @param {'inside' | 'outside'} classification

 * @param {'normal' | 'dark'} [shadeVariant]

 */

export function resolveWallSideMaterial(

  materials,

  bundle,

  config,

  classification,

  shadeVariant = 'normal',

) {

  const normalized = normalizeMaterialConfig(config);

  const isInside = classification === 'inside';



  if (normalized.materialMode === 'texture' && bundle?.loaded) {

    const entry = getTextureCatalogEntry(normalized.textureId);

    const baseTexture = resolveWallTextureMap(bundle, entry?.id ?? normalized.textureId, classification);

    if (baseTexture) {

      return getOrCreateMaterial({

        key: `wall-texture:${entry?.id ?? normalized.textureId}:${classification}`,

        baseMap: baseTexture,

        colorHex: '#ffffff',

        shadeVariant,

      });

    }

  }



  if (normalized.materialMode === 'color') {

    return getOrCreateMaterial({

      key: `wall-color:${normalized.color}:${classification}`,

      baseMap: null,

      colorHex: normalized.color,

      shadeVariant,

    });

  }



  return isInside

    ? materials.wallInside

    : materials.wallOutside;

}



/**

 * @param {import('./floorPlanThreeTextures.js').FloorPlanMaterialSet} materials

 * @param {import('./floorPlanThreeTextures.js').BaseTextureBundle | null | undefined} bundle

 * @param {import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig | null | undefined} config

 */

/** @type {Map<string, THREE.MeshStandardMaterial>} */
const floorMaterialCache = new Map();

/**
 * @param {string} key
 * @param {{ map?: THREE.Texture | null; colorHex: string }} params
 */
function getOrCreateFloorMaterial(key, { map = null, colorHex }) {
  const cached = floorMaterialCache.get(key);
  if (cached) return cached;
  const material = createFloorSurfaceMaterial({ map, colorHex });
  floorMaterialCache.set(key, material);
  return material;
}

export function resolveFloorMaterial(materials, bundle, config) {
  const normalized = normalizeMaterialConfig(config);

  if (normalized.materialMode === 'texture' && bundle?.loaded) {
    const entry = getTextureCatalogEntry(normalized.textureId);
    const baseTexture = getCatalogTextureById(entry?.id ?? normalized.textureId, bundle);
    if (baseTexture) {
      return getOrCreateFloorMaterial(`floor-texture:${entry?.id ?? normalized.textureId}`, {
        map: baseTexture,
        colorHex: '#ffffff',
      });
    }
  }

  if (normalized.materialMode === 'color') {
    return getOrCreateFloorMaterial(`floor-color:${normalized.color}`, {
      colorHex: normalized.color,
    });
  }

  return materials.floor;
}



export function clearFloorPlanMaterialResolverCache() {
  [...materialCache.keys()].forEach(disposeCachedMaterial);
  floorMaterialCache.forEach((material) => {
    if (material.map) material.map.dispose();
    material.dispose();
  });
  floorMaterialCache.clear();
}

